import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Facebook, Clock, Lock, ShieldCheck, CheckCircle } from 'lucide-react';
import { ProtectedEmail, ProtectedPhone } from './ProtectedContact';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">SA</span>
              </div>
              <div>
                <p className="font-semibold leading-tight text-sm">Studio Professionale Azzano Cantarutti SRL</p>
                <p className="text-xs text-slate-400">Società tra professionisti</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              <li><Link to="/chi-sono" className="text-slate-400 hover:text-white transition-colors text-sm">{t('nav.chiSono')}</Link></li>
              <li><Link to="/servizi" className="text-slate-400 hover:text-white transition-colors text-sm">{t('nav.servizi')}</Link></li>
              <li><Link to="/consulenza" className="text-slate-400 hover:text-white transition-colors text-sm">{t('nav.consulenza')}</Link></li>
              <li><Link to="/area-clienti" className="text-slate-400 hover:text-white transition-colors text-sm">{t('nav.areaClienti')}</Link></li>
              <li><Link to="/contatti" className="text-slate-400 hover:text-white transition-colors text-sm">{t('nav.contatti')}</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-6">{t('footer.services')}</h4>
            <ul className="space-y-3">
              <li className="text-slate-400 text-sm">{t('footer.consultingStartup')}</li>
              <li className="text-slate-400 text-sm">{t('footer.ecommerceWeb')}</li>
              <li className="text-slate-400 text-sm">{t('footer.internationalTax')}</li>
              <li className="text-slate-400 text-sm">{t('footer.accounting')}</li>
              <li className="text-slate-400 text-sm">{t('footer.digitalInnovation')}</li>
              <li className="text-slate-400 text-sm">{t('footer.eInvoicing')}</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-6">{t('footer.contact')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">
                  Piazza Pellegrino 8<br />
                  33038 San Daniele del Friuli (UD)
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                <ProtectedPhone parts={['+39', '0427', '40326']} display="+39 0427 40326" className="text-slate-400 hover:text-white transition-colors text-sm" />
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                <ProtectedEmail user="info" domain="studioazzanostp.it" className="text-slate-400 hover:text-white transition-colors text-sm" />
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                <span className="text-slate-400 text-sm">
                  Lun-Ven: 9:00 - 18:00
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-sm">
            © 2024 - {currentYear} Studio Professionale Azzano Cantarutti SRL - Società tra professionisti. {t('footer.rights')}
          </p>
          <p className="text-slate-500 text-xs mt-1">
            P.IVA: IT03196730307 | C.F. 03196730307
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors text-sm">
              Privacy Policy
            </Link>
            <Link to="/cookie-policy" className="text-slate-400 hover:text-white transition-colors text-sm">
              Cookie Policy
            </Link>
            <a 
              href="https://www.facebook.com/studioazzanocantarutti/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="facebook-link"
            >
              <Facebook className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 pt-6 border-t border-slate-800">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            <div className="flex items-center gap-2 text-slate-400">
              <Lock className="w-4 h-4 text-emerald-400" />
              <span className="text-xs">{t('footer.securePayments', 'Pagamenti Sicuri')}</span>
              <svg className="h-5 w-auto opacity-60" viewBox="0 0 60 25" fill="currentColor">
                <text x="0" y="18" fontSize="15" fontWeight="bold" fill="#635BFF">stripe</text>
              </svg>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="text-xs">{t('footer.sslProtected', 'Connessione Protetta SSL')}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-xs">GDPR Compliant</span>
            </div>
          </div>
        </div>

        {/* Made with Love Badge */}
        <div className="mt-8 pt-6 border-t border-slate-800 flex justify-center">
          <a
            href="https://telcavoip.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-full transition-colors group"
            data-testid="telcavoip-badge"
          >
            <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <span className="text-slate-400 text-sm group-hover:text-white transition-colors">
              Made with Love by <span className="font-semibold text-indigo-400">TelcaVoIP AI</span>
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
